import { useMemo, useState } from "react";
import { LayoutGrid, Undo2 } from "lucide-react";
import type { CaseLayout, InboxFieldResponse } from "@/generated";
import { Button } from "@/components/shadcn/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import BaseDialog from "@/components/Modals/BaseDialog";
import {
  CaseLayoutEditor,
  mockValueForField,
  seedLayoutDraft,
} from "@/components/CaseManagement/CaseLayoutEditor";
import {
  fromApi,
  heightLookup,
  projectLayout,
  toApi,
  type EditableLayout,
} from "@/components/CaseManagement/caseLayout";
import type { ExtractionField } from "./ExtractionSchemaBuilderConfig";

const rowKeyOf = (row: ExtractionField, index: number): string =>
  String(row.id ?? row.Label ?? index);

const rowToPseudoField = (
  row: ExtractionField,
  index: number
): InboxFieldResponse => {
  const type = row.type === "attachment" ? "file" : row.type;
  return {
    id: rowKeyOf(row, index),
    inbox_id: "task",
    name: row.Label,
    label: row.Label,
    type,
    required: false,
    group: null,
    order: index,
    description: null,
    case_view: "editable",
    flow_editable: true,
    options: row.options ?? null,
    created_at: "",
    updated_at: "",
  } as unknown as InboxFieldResponse;
};

/** "Design layout" affordance for a task's properties: arranges the schema
 * rows (rows + widths, no groups) in the same editor the case layout uses.
 * The layout persists on a sibling config key and overrides the inherited
 * case layout in the task view. */
export const TaskLayoutDesigner = ({
  rows,
  value,
  onChange,
  onRowsChange,
  inheritedLayout,
  isDisabled,
  iconOnly,
}: {
  rows: ExtractionField[];
  value: unknown;
  onChange: (layout: unknown) => void;
  /** Persists id backfills for legacy rows saved before rows had ids. */
  onRowsChange?: (rows: ExtractionField[]) => void;
  /** The inbox's case layout; without an own layout, linked rows inherit
   * it at runtime, so the automatic seed starts from it. */
  inheritedLayout?: unknown;
  isDisabled?: boolean;
  /** Compact square trigger for inline placement in an actions row. */
  iconOnly?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<EditableLayout | null>(null);

  const pseudoFields = useMemo(
    () => rows.filter((r) => r.Label).map(rowToPseudoField),
    [rows]
  );
  const previewValues = useMemo(
    () =>
      Object.fromEntries(pseudoFields.map((f) => [f.id, mockValueForField(f)])),
    [pseudoFields]
  );

  const seedFromInheritance = (
    designRows: ExtractionField[]
  ): CaseLayout | null => {
    const inherited = fromApi(inheritedLayout as CaseLayout | null);
    if (!inherited) return null;
    const labeled = designRows.filter((r) => r.Label);
    const keyByFieldId = new Map<string, string>();
    labeled.forEach((row, index) => {
      if (row.field_id) keyByFieldId.set(row.field_id, rowKeyOf(row, index));
    });
    if (keyByFieldId.size === 0) return null;
    const rowHeights = heightLookup(labeled.map(rowToPseudoField));
    const projected = projectLayout(
      inherited,
      new Set(keyByFieldId.keys()),
      (fieldId) => {
        const key = keyByFieldId.get(fieldId);
        return key ? rowHeights(key) : 1;
      }
    );
    return toApi({
      version: 1,
      sections: projected.sections.map((section) => ({
        title: section.title,
        cells: section.cells.map((cell) => ({
          ...cell,
          field_id: keyByFieldId.get(cell.field_id) ?? cell.field_id,
        })),
      })),
    }) as CaseLayout;
  };

  const openDialog = () => {
    // Legacy rows have no id, which would key the layout on the editable
    // Label; backfill ids before designing so cells survive renames.
    let designRows = rows;
    if (onRowsChange && rows.some((r) => !r.id)) {
      designRows = rows.map((r) =>
        r.id ? r : { ...r, id: crypto.randomUUID() }
      );
      onRowsChange(designRows);
    }
    const stored =
      value &&
      typeof value === "object" &&
      (value as { sections?: unknown[] }).sections?.length
        ? (value as CaseLayout)
        : null;
    setDraft(
      seedLayoutDraft(
        stored ?? seedFromInheritance(designRows),
        designRows.filter((r) => r.Label).map(rowToPseudoField)
      )
    );
    setOpen(true);
  };

  const hasCustomLayout = Boolean(
    value &&
    typeof value === "object" &&
    (value as { sections?: unknown[] }).sections?.length
  );

  return (
    <>
      {iconOnly ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              disabled={isDisabled || pseudoFields.length === 0}
              onClick={openDialog}
            >
              <LayoutGrid />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {hasCustomLayout ? "Edit task layout" : "Design task layout"}
          </TooltipContent>
        </Tooltip>
      ) : (
        <div>
          <Button
            type="button"
            variant="outline"
            disabled={isDisabled || pseudoFields.length === 0}
            onClick={openDialog}
          >
            <LayoutGrid />
            {hasCustomLayout ? "Edit task layout" : "Design task layout"}
          </Button>
        </div>
      )}
      <BaseDialog
        isOpen={open}
        onOpenChange={setOpen}
        size="4xl"
        className="h-[85%]"
        title="Task layout"
        description="Arrange how the task's properties appear to the person completing it. Without a layout, linked properties follow the case layout."
        footerLeft={
          <Button
            variant="ghost"
            onClick={() => {
              onChange({});
              setOpen(false);
            }}
          >
            <Undo2 />
            Reset to automatic
          </Button>
        }
        footerActions={[
          {
            label: "Cancel",
            props: { variant: "outline", onClick: () => setOpen(false) },
          },
          {
            label: "Save layout",
            props: {
              disabled: !draft,
              onClick: () => {
                if (draft) onChange(toApi(draft));
                setOpen(false);
              },
            },
          },
        ]}
      >
        {draft && (
          // px-4: room for the resize-handle overhang; overflow-x hidden so
          // mid-drag previews can't flash a horizontal scrollbar.
          <div className="overflow-x-hidden px-4 py-2">
            <CaseLayoutEditor
              draft={draft}
              onChange={setDraft}
              fields={pseudoFields}
              previewValues={previewValues}
              allowGroups={false}
            />
          </div>
        )}
      </BaseDialog>
    </>
  );
};
